import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Image as ImageIcon, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { inventoryService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';
import { IMAGE_MAX_BYTES, IMAGE_MAX_LABEL, imageTooLargeMessage } from './imageProfile';

interface Props {
  itemId: string | null;
}

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Hero / banner image — a single wide image for the storefront product
 * page (distinct from the photo gallery). Upload replaces the previous
 * one. In add mode (no itemId yet) we prompt to save first, matching the
 * gallery tab. Exposed to the portal as `heroImageUrl`.
 */
export function ItemHeroTab({ itemId }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const heroQuery = useQuery({
    queryKey: ['inventory', 'hero', itemId],
    queryFn: () => inventoryService.getHeroImage(itemId!),
    enabled: !!itemId,
  });

  const upload = useMutation({
    mutationFn: (file: File) => inventoryService.uploadHeroImage(itemId!, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'hero', itemId] });
      toast.success('Hero banner updated');
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; message?: string };
      if (e?.status === 413 || (typeof e?.message === 'string' && /too large|payload/i.test(e.message))) {
        toast.error(imageTooLargeMessage());
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    },
  });

  const remove = useMutation({
    mutationFn: () => inventoryService.deleteHeroImage(itemId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory', 'hero', itemId] });
      toast.success('Hero banner removed');
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not remove'),
  });

  const handleFile = (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (!file || !itemId) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error(`Type ${file.type || 'unknown'} not supported. Use JPEG, PNG, WebP or GIF.`);
      return;
    }
    if (file.size > IMAGE_MAX_BYTES) {
      toast.error(imageTooLargeMessage());
      return;
    }
    upload.mutate(file);
  };

  // Add mode — no item to attach to yet.
  if (!itemId) {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 py-10 text-center">
        <ImageIcon className="mx-auto h-5 w-5 text-muted-foreground/60" />
        <div className="mt-2 text-[13px] font-medium text-foreground">Save the item first</div>
        <div className="mx-auto mt-1 max-w-sm text-[12px] text-muted-foreground">
          Once the item is created you can upload its hero banner here. It shows as the wide
          header image on the storefront product page.
        </div>
      </div>
    );
  }

  const hero = heroQuery.data ?? null;
  const busy = upload.isPending || remove.isPending;

  return (
    <div className="space-y-4">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files);
          e.target.value = '';
        }}
      />

      {heroQuery.isLoading ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : hero ? (
        <div className="space-y-3">
          {/* Wide preview in banner aspect */}
          <div className="relative overflow-hidden rounded-lg border border-border/70 bg-muted">
            <div className="aspect-[3/1] w-full">
              <img
                src={hero.url}
                alt="Hero banner"
                className="h-full w-full object-cover"
              />
            </div>
            {busy && (
              <div className="absolute inset-0 grid place-items-center bg-background/60">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[12px] text-muted-foreground">
              <span className="font-medium text-foreground">{hero.filename}</span>
              {hero.width && hero.height ? ` · ${hero.width}×${hero.height}` : ''}
              {` · ${fmt.compact(hero.size)}B`}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                disabled={busy}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Replace
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => remove.mutate()}
                disabled={busy}
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        // Empty — wide drop zone
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files);
          }}
          className={cn(
            'flex aspect-[3/1] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed text-center transition-colors',
            dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:border-primary/50',
          )}
        >
          {upload.isPending ? (
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          ) : (
            <Upload className="h-6 w-6 text-muted-foreground" />
          )}
          <div className="mt-2 text-[13px] text-foreground">
            Drag a banner here, or <span className="font-medium text-primary">choose a file</span>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            Wide image recommended (≈ 1600×600, 3:1) · JPEG, PNG, WebP, GIF · max {IMAGE_MAX_LABEL}
          </div>
        </button>
      )}

      <p className="text-[11.5px] leading-relaxed text-muted-foreground">
        The hero banner is the wide header image on the storefront product page. It&apos;s
        separate from the photo gallery and exposed to the portal as{' '}
        <code className="rounded bg-muted px-1 py-0.5 text-[10.5px]">heroImageUrl</code>.
      </p>
    </div>
  );
}
